export type PaymentActions = {
  showAcceptView: () => void;
  showDeclineView: () => void;
  showEnterView: () => void;
  showHardErrorView: () => void;
  showPendingView: () => void;
  showSoftErrorView: () => void;
  showSuccessView: () => void;
}

export type PaymentViewProps = {
  actions: PaymentActions;
}

export enum PAYMENTS_VIEWS {
  ACCEPT = 'accept',
  DECLINE = 'decline',
  ENTER = 'enter',
  HARD_ERROR = 'hardError',
  PENDING = 'pending',
  SOFT_ERROR = 'softError',
  SUCCESS = 'success',
}

export type PriceBreakdownType = 'financing_amount' | 'seller_amount' | 'dealer_amount' | 'vehicle_amount' | 'broker_fee_amount' | 'transport_fee_amount' | 'total_fee_amount';
export type PostSaleOfferState = 'approved' | 'pending' | 'rejected';
export type PostSaleOfferType = 'closing_bid' | 'counter_offer';
export type PostSaleOfferApprovalsUserType = 'dealer' | 'seller';
export type PostSaleOfferUserType = PostSaleOfferApprovalsUserType & 'agent';
export type PaymentStatus = 'initial' | 'pending' | 'failed' | 'done' | 'errored' | 'not_required';
export type PaymentType = 'dealer_to_mw' | 'mw_to_seller' | 'mw_to_finance_house' | 'seller_to_mw';
export type LongPoolingStatus = 'PENDING' | 'MAX_RETRIES' | 'SUCCESS'

export type PriceBreakdown = {
  amount: number;
  amountFormatted?: string;
  createdAt: string;
  id: number;
  type: PriceBreakdownType;
  updatedAt: string;
};

export type PostSaleOfferApprovals = {
  createdAt: string;
  id: number;
  postSaleOfferId: number;
  updatedAt: string;
  userType: PostSaleOfferApprovalsUserType;
}

type LatestPostSaleOfferBase = {
  amount: number;
  approvals: PostSaleOfferApprovals[];
  closingBidId: number;
  createdAt: string;
  enquiryId: number;
  id: number;
  priceBreakdown: PriceBreakdown[];
  state: PostSaleOfferState;
  type: PostSaleOfferType;
  userId: number;
  userType: PostSaleOfferUserType;
}

type LatestPostSaleOfferInclude = 'dealerPaymentCompleted' | 'dealerName';

type LatestPostSaleOfferIncludeCases<U> = U extends 'dealerPaymentCompleted'
  ? { dealerPaymentCompleted: boolean; dealerPaymentCompletedAt: string }
  : U extends 'dealerName'
    ? { dealerName: string }
    : never;

export type LatestPostSaleOffer<Params extends Record<'include', LatestPostSaleOfferInclude> | never = never> =
  Prettify<LatestPostSaleOfferBase & UnionToIntersection<
    Params extends Record<'include', infer U> ? LatestPostSaleOfferIncludeCases<U> : never>
  >;

export type Beneficiary = {
  accountNumber: string;
  id: string;
  isBusiness: boolean;
  name: string;
  reference: string;
  sortCode: string;
}

export type PaymentStatuses = {
  createdAt: string;
  isManual: boolean;
  reference: string;
  status: PaymentStatus;
  type: PaymentType;
}

export type PaymentData = PaymentStatuses & {
  amount: number;
  dealerId: number;
  id: number;
  paidAt: string;
  postSaleOfferId: number;
  reference: string;
  sellerId: number;
}

export interface PaymentContextState {
  accountNumber: string;
  approvals: PostSaleOfferApprovals[];
  dealerName: string;
  financingAmount: number;
  financingAmountFormatted?: string;
  hasNegativeEquity: boolean;
  offerAmount: number;
  paymentStatus: PaymentStatus | null;
  postSaleOfferState: PostSaleOfferState | null;
  reference: string | null;
  sellerAmount: number;
  sellerAmountFormatted?: string;
  vehicleAmount: number;
  vehicleAmountFormatted?: string;
}

export const LONG_POOLING_DELAY_TIME = 5000;
export const LONG_POOLING_MAX_RETRIES = 20;
export const LONG_POOLING_EXPIRED_TIME = 1000 * 60 * 60 * 2; // 2h
export const MWToSellerKey: PaymentType = 'mw_to_seller';
export const SellerToMWKey: PaymentType = 'seller_to_mw';

export type FinanceStatusStatuses = 'paid' | 'unpaid' | 'partial' | 'overpaid';

export type FinanceStatusResponse<NE extends boolean = boolean> = Prettify<(
  NE extends true ? {
    financeStatus: {
      seller: {
        amountOwed: number;
        amountPaid: number;
        amountRemaining: number;
        status: FinanceStatusStatuses;
      };
    };
    isNegativeEquity: true;
  } : {
    isNegativeEquity: false;
  }
) & {
  enquiryId: number;
  postSaleOfferId: number;
}>;
